import { api } from './api';

export type PaymentMethod = 'bank_transfer' | 'cheque' | 'card' | 'cash' | 'online' | 'other';

export interface PaymentAllocation {
  id: string;
  paymentId: string;
  feeNoteId: string;
  amountApplied: string;
  isWriteOff: boolean;
  createdAt: string;
  feeNote: {
    id: string;
    feeNoteNumber: string;
    status: string;
    total: string;
    amountPaid: string;
    case: { id: string; caseNumber: string; caseName: string } | null;
  };
}

export interface Payment {
  id: string;
  chambersId: string;
  paidByContactId: string | null;
  payerName: string | null;
  paymentDate: string;
  totalAmount: string;
  currency: string;
  paymentMethod: PaymentMethod;
  referenceNumber: string | null;
  depositAccountId: string | null;
  notes: string | null;
  recordedById: string;
  voidedAt: string | null;
  voidedById: string | null;
  voidReason: string | null;
  createdAt: string;
  updatedAt: string;
  paidByContact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    company: string | null;
    type: string;
  } | null;
  depositAccount: {
    id: string;
    name: string;
    type: string;
    bankName: string | null;
    accountNumberLast4: string | null;
  } | null;
  recordedBy: { id: string; firstName: string; lastName: string };
  voidedBy: { id: string; firstName: string; lastName: string } | null;
  allocations: PaymentAllocation[];
}

export interface OpenInvoice {
  id: string;
  feeNoteNumber: string;
  status: string;
  issueDate: string;
  dueDate: string;
  total: string;
  amountPaid: string;
  currency: string;
  balance: string;
  case: {
    id: string;
    caseNumber: string;
    caseName: string;
    instructingSolicitorId: string | null;
  } | null;
}

export interface ListPaymentsQuery {
  contactId?: string;
  feeNoteId?: string;
  caseId?: string;
  paymentMethod?: PaymentMethod;
  dateFrom?: string;
  dateTo?: string;
  includeVoided?: 'true' | 'false';
  page?: number;
  limit?: number;
}

export interface ListPaymentsResponse {
  payments: Payment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function fetchPayments(query: ListPaymentsQuery = {}): Promise<ListPaymentsResponse> {
  const res = await api.get<ListPaymentsResponse>('/payments', { params: query });
  return res.data;
}

export async function fetchPayment(id: string): Promise<Payment> {
  const res = await api.get<{ payment: Payment }>(`/payments/${id}`);
  return res.data.payment;
}

export async function fetchOpenInvoices(query: { contactId?: string; caseId?: string } = {}): Promise<OpenInvoice[]> {
  const res = await api.get<{ invoices: OpenInvoice[] }>('/payments/open-invoices', { params: query });
  return res.data.invoices;
}

export interface CreatePaymentPayload {
  paidByContactId?: string | null;
  payerName?: string | null;
  paymentDate: string;
  totalAmount: number;
  currency?: string;
  paymentMethod: PaymentMethod;
  referenceNumber?: string | null;
  depositAccountId?: string | null;
  notes?: string | null;
  allocations: Array<{
    feeNoteId: string;
    amountApplied: number;
    isWriteOff: boolean;
  }>;
}

export async function createPayment(payload: CreatePaymentPayload): Promise<Payment> {
  const res = await api.post<{ payment: Payment }>('/payments', payload);
  return res.data.payment;
}

export async function voidPayment(id: string, reason: string): Promise<Payment> {
  const res = await api.post<{ payment: Payment }>(`/payments/${id}/void`, { reason });
  return res.data.payment;
}

// ─── Unallocated funds + allocate-existing-payment ───

export interface UnallocatedFundItem {
  paymentId: string;
  payer: string | null;
  paymentDate: string;
  currency: string;
  total: number;
  allocated: number;
  unallocated: number;
  appliedTo: string[];
}

export interface UnallocatedFunds {
  currency: string;
  totalUnallocated: number;
  count: number;
  items: UnallocatedFundItem[];
}

export async function fetchUnallocatedFunds(): Promise<UnallocatedFunds> {
  const res = await api.get<{ data: UnallocatedFunds }>('/payments/unallocated');
  return res.data.data;
}

/** Allocate the still-unallocated remainder of an existing payment to fee notes. */
export async function allocatePayment(
  paymentId: string,
  allocations: Array<{ feeNoteId: string; amountApplied: number; isWriteOff: boolean }>,
): Promise<Payment> {
  const res = await api.post<{ payment: Payment }>(`/payments/${paymentId}/allocate`, { allocations });
  return res.data.payment;
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
  card: 'Card',
  cash: 'Cash',
  online: 'Online',
  other: 'Other',
};
